"use client";

import { motion } from "framer-motion";
import ShadowCard from "@/components/common/ShadowCard";
import {
  giftCardProgramData,
  GiftCardProgramItem,
} from "@/data/giftCardProgramData";

interface CardItemProps {
  item: GiftCardProgramItem;
  index: number;
}

function CardItem({ item, index }: CardItemProps) {
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.6,
        delay: index * 0.15,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      <ShadowCard className="from-brand-red to-brand-dark-red h-full bg-linear-to-r p-[18px]">
        <div className="flex flex-col gap-2.5">
          {/* Icon */}
          <Icon className="h-10 w-10 text-white" />

          {/* Title */}
          <h3 className="font-raleway text-2xl leading-tight font-bold text-white md:text-[32px]">
            {item.title}
          </h3>

          {/* Description */}
          <p className="font-raleway text-base leading-[1.3] text-white/80">
            {item.description}
          </p>
        </div>
      </ShadowCard>
    </motion.div>
  );
}

export default function GiftCardProgramSection() {
  return (
    <section className="bg-brand-black w-full px-5 py-16 md:px-[100px] md:py-48">
      <div>
        {/* Header Section */}
        <motion.div
          className="mb-10 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="font-raleway mb-2.5 text-2xl font-bold text-white md:text-[32px]">
            Georgia COAM Gift Card Program
          </h2>
          <p className="font-raleway mx-auto max-w-5xl text-base leading-[1.3] text-gray-300">
            The Georgia Lottery Corporation (GLC) has expanded its gift card
            redemption program for Class B COAMs (Coin-Operated Amusement
            Machines), allowing players to redeem their winnings not just for
            merchandise or free play, but also for Visa or MasterCard gift cards
            issued through the official GLC program. This marks a significant
            step forward in offering more flexible and appealing prize options
            to players across Georgia.
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-10">
          {giftCardProgramData.map((item, index) => (
            <CardItem key={item.title} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
